// @ts-check
import { Maximize2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Small trigger button that opens the given image at full/native resolution in a
 * full-screen overlay. Renders as a standalone icon button (not a wrapper around the
 * thumbnail) so it can be dropped next to existing hover overlays (replace, edit,
 * remove, etc.) without stealing their click target.
 *
 * Place inside a `relative` (and usually `group`, for hover-reveal) container.
 */
const ImagePreviewButton = ({ src, alt = '', className = '' }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open]);

  if (!src) return null;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        title="Preview full quality"
        className={
          className ||
          'absolute top-2 left-2 p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors opacity-0 pointer-coarse:opacity-100 group-hover:opacity-100 z-10'
        }
      >
        <Maximize2 size={14} />
      </button>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4"
            onClick={() => setOpen(false)}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              title="Close"
              className="absolute top-4 right-4 text-white hover:text-muted-foreground/50"
            >
              <X size={28} />
            </button>
            <img
              src={src}
              alt={alt}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

export default ImagePreviewButton;
